import MCPCard from "./MCPCard";
import AddCustomMCPForm from "./AddCustomMCPForm";
import CustomMCPUpsell from "./CustomMCPUpsell";
import { useMCPServers } from "@/hooks/useMCPServers";
import { useMemo, useState } from "react";
import { Button } from "../ui/button";
import { Plus } from "lucide-react";
import { useSubscriptionInfo } from "@/hooks/pricing/useSubscriptionInfo";
import { track } from "@/utils/events/tracker";

export default function MCPStore() {
  const { servers, featuredMCPs: featuredMCPsFromDB } = useMCPServers();
  const [showAddForm, setShowAddForm] = useState(false);
  const { subscriptionTier } = useSubscriptionInfo();

  const handleOpenAddForm = () => {
    track("MCP", {
      step: "custom_connection_form_opened",
      source: "custom",
    });
    setShowAddForm(true);
  };

  // Check if user has access to custom MCP servers (Scale or higher)
  const hasCustomMCPAccess = useMemo(() => {
    if (!subscriptionTier) return false;
    // Block Free and Pro users
    const blockedTiers = ["free", "pro", "new_pro"];
    return !blockedTiers.includes(subscriptionTier);
  }, [subscriptionTier]);

  const { featuredMCPs, customMCPs } = useMemo(() => {
    // Create a map of server URLs to connections
    const serverConnectionsMap = new Map(servers.map((s) => [s.server_url, s]));

    // Build featured MCPs list with connection status
    const featuredMCPs = featuredMCPsFromDB.map((featured) => {
      const connection = serverConnectionsMap.get(featured.server_url);
      return {
        id: connection?.id || featured.id,
        name: featured.display_name,
        displayName: featured.display_name,
        icon: featured.icon_url,
        serverUrl: featured.server_url,
        description: featured.description,
        instructions: featured.instructions,
        docsUrl: featured.docs_url,
        transportType: featured.transport_type,
        connectionStatus: connection?.connection_status,
      };
    });

    // Build custom MCPs list (servers not in featured list)
    const featuredServerUrls = new Set(
      featuredMCPsFromDB.map((f) => f.server_url),
    );
    const customMCPs = servers
      .filter((s) => !featuredServerUrls.has(s.server_url))
      .map((s) => ({
        id: s.id,
        name: s.server_name,
        displayName: s.server_name,
        icon: "/icons/mcp.svg",
        serverUrl: s.server_url,
        description: s.server_url,
        instructions: [],
        docsUrl: undefined,
        transportType: "http" as const,
        connectionStatus: s.connection_status,
      }));

    return { featuredMCPs, customMCPs };
  }, [servers, featuredMCPsFromDB]);

  return (
    <div className="flex flex-col h-full bg-background overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-border shrink-0">
        <img src="/icons/mcp.svg" alt="MCP" className="h-8 w-8" />
        <div className="flex-1">
          <h2 className="text-base font-semibold text-foreground">MCP Store</h2>
          <p className="text-xs text-muted-foreground">
            Model Context Protocol Servers
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5 min-h-0">
        {/* Featured MCPs Section */}
        <div className="mb-8">
          <h3 className="text-xs font-semibold text-muted-foreground mb-4">
            Popular MCPs
          </h3>
          <div className="grid grid-cols-3 gap-4 auto-rows-fr">
            {featuredMCPs.map((mcp) => (
              <div key={mcp.id} className="min-h-[140px]">
                <MCPCard
                  config={mcp}
                  isConnected={mcp.connectionStatus === "connected"}
                  isDisconnected={
                    mcp.connectionStatus === "disconnected" ||
                    mcp.connectionStatus === "error"
                  }
                />
              </div>
            ))}
          </div>
        </div>

        {/* Custom MCP Servers Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs font-semibold text-muted-foreground">
              Custom MCP Servers
            </h3>
          </div>

          {!hasCustomMCPAccess ? (
            <CustomMCPUpsell />
          ) : showAddForm ? (
            <AddCustomMCPForm onClose={() => setShowAddForm(false)} />
          ) : customMCPs.length > 0 ? (
            <div className="grid grid-cols-3 gap-4 auto-rows-fr">
              {customMCPs.map((mcp) => (
                <div key={mcp.id} className="min-h-[140px]">
                  <MCPCard
                    config={mcp}
                    isConnected={mcp.connectionStatus === "connected"}
                    isDisconnected={
                      mcp.connectionStatus === "disconnected" ||
                      mcp.connectionStatus === "error"
                    }
                  />
                </div>
              ))}
              {/* Add Server Card */}
              <div className="min-h-[140px]">
                <button
                  onClick={handleOpenAddForm}
                  className="border border-dashed border-border rounded-lg p-4 hover:border-primary-0/50 transition-colors bg-background h-full w-full flex flex-col items-center justify-center gap-2 group"
                >
                  <Plus className="h-6 w-6 text-muted-foreground group-hover:text-foreground transition-colors" />
                  <span className="text-sm font-medium text-muted-foreground group-hover:text-foreground transition-colors">
                    Add Server
                  </span>
                </button>
              </div>
            </div>
          ) : (
            <div className="border border-border rounded-lg p-8 text-center">
              <div className="flex flex-col items-center gap-3">
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">
                    No custom servers yet
                  </p>
                  <p className="text-xs text-muted-foreground mb-4">
                    Add your own HTTP-based MCP server to get started
                  </p>
                </div>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleOpenAddForm}
                  className="gap-1.5"
                >
                  <Plus className="h-4 w-4" />
                  Add Custom Server
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
